import { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { SeoKeywordOpportunity, OpportunityStatus, FixGuidance, AiSuggestion, SeoTask } from '@/types/api/seo';
import { mapOpportunitiesToPlaybook } from './playbookConfig';
import PlaybookPhase from './PlaybookPhase';
import OpportunityCard from './OpportunityCard';

type Props = {
  opportunities: SeoKeywordOpportunity[];
  isLoading: boolean;
  isAnalyzing: boolean;
  onAnalyze: () => void;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onGetFixGuidance: (opportunity: SeoKeywordOpportunity) => Promise<FixGuidance | null>;
  onGetSuggestions: (opportunity: SeoKeywordOpportunity) => Promise<AiSuggestion[] | null>;
  onApplyToWordPress: (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null) => Promise<{ success: boolean; error?: string }>;
  wpConnected: boolean;
  rankingPageUrl: string | null;
  currentMetrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null };
  livePosition?: number | null;
  onGenerateStepSuggestions: (stepId: string, stepLabel: string, stepDescription: string) => Promise<void>;
  onAddTask: (suggestion: AiSuggestion, opportunityId: string) => Promise<boolean>;
  existingTasks: SeoTask[];
};

const KeywordOpportunitiesComponent = function ({
  opportunities,
  isLoading,
  isAnalyzing,
  onAnalyze,
  onStatusChange,
  onGetFixGuidance,
  onGetSuggestions,
  onApplyToWordPress,
  wpConnected,
  rankingPageUrl,
  currentMetrics,
  livePosition: livePos,
  onGenerateStepSuggestions,
  onAddTask,
  existingTasks,
}: Props): JSX.Element {
  const playbook = useMemo(
    () => mapOpportunitiesToPlaybook(opportunities, livePos),
    [opportunities, livePos]
  );

  const progressPercent = playbook.totalSteps > 0
    ? Math.round((playbook.completedSteps / playbook.totalSteps) * 100)
    : 0;

  // Find first incomplete phase
  const firstIncompletePhaseId = playbook.phases.find(
    (p) => p.completedSteps < p.totalSteps
  )?.id ?? playbook.phases[0]?.id;

  const allComplete = playbook.completedSteps === playbook.totalSteps && opportunities.length > 0
    && playbook.phases.every((p) => p.stepsData.every((s) => s.status === 'completed'));

  return (
    <Box sx={{
      border: '2px solid',
      borderColor: '#054AD930',
      borderRadius: 3,
      p: 3,
      backgroundColor: '#fff',
      mb: 4,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <RocketLaunchIcon sx={{ fontSize: 22, color: '#054AD9' }} />
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: 16 }}>
          Ranking Playbook
        </Typography>
        <Tooltip title={isAnalyzing ? 'Analyzing...' : 'Run AI analysis to detect opportunities'}>
          <span style={{ marginLeft: 'auto' }}>
            <Button
              size="small"
              startIcon={isAnalyzing ? <CircularProgress size={14} sx={{ color: '#054AD9' }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={onAnalyze}
              disabled={isAnalyzing}
              sx={{ fontSize: 11, textTransform: 'none', color: '#054AD9' }}
            >
              {opportunities.length === 0 ? 'Analyze & Build Plan' : 'Re-analyze'}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Your step-by-step plan to rank this keyword in the top 3 on Google.
      </Typography>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, justifyContent: 'center' }}>
          <CircularProgress size={16} sx={{ color: '#054AD9' }} />
          <Typography variant="caption" color="text.secondary">Loading your ranking plan...</Typography>
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && opportunities.length === 0 && !isAnalyzing && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <RocketLaunchIcon sx={{ fontSize: 48, color: '#054AD920', mb: 1.5 }} />
          <Typography variant="body1" component="div" fontWeight={600} sx={{ mb: 0.5 }}>
            Build Your Ranking Plan
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 2.5, maxWidth: 400, mx: 'auto' }}>
            We'll analyze your page, competitors, and Google's requirements to create a step-by-step plan to reach the top 3.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RocketLaunchIcon />}
            onClick={onAnalyze}
            sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none', fontSize: 13, px: 3, py: 1 }}
          >
            Analyze & Build Plan
          </Button>
        </Box>
      )}

      {/* Analyzing */}
      {isAnalyzing && (
        <Box sx={{ py: 2, px: 2, backgroundColor: '#FFF0F5', borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={14} sx={{ color: '#054AD9' }} />
            <Typography variant="caption" fontWeight={600} color="#054AD9">Building your Ranking Plan...</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5 }}>
            1. Checking live Google position via SerpAPI...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5 }}>
            2. Fetching real Google competitors & SERP features...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5 }}>
            3. Analyzing your page content (title, meta, headings, word count)...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5 }}>
            4. Checking Core Web Vitals via PageSpeed API...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5 }}>
            5. Running 13 data checks + GPT enhancement...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', pl: 3.5, mt: 0.5 }}>
            This may take 30-60 seconds.
          </Typography>
        </Box>
      )}

      {/* Playbook content */}
      {!isLoading && opportunities.length > 0 && (
        <>
          {/* Overall progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 12 }}>
                {allComplete ? 'All steps complete!' : `${playbook.completedSteps} of ${playbook.totalSteps} steps complete`}
              </Typography>
              <Typography variant="caption" fontWeight={700} color="#054AD9" sx={{ fontSize: 12 }}>
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#F0F0F0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: allComplete
                    ? '#0D9D58'
                    : 'linear-gradient(90deg, #7C3AED, #054AD9, #4285F4, #0D9D58)',
                },
              }}
            />
          </Box>

          {/* All complete banner */}
          {allComplete && (
            <Box sx={{ p: 2, mb: 2, backgroundColor: '#E6F4EA', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" component="div" fontWeight={600} color="#0D9D58">
                All optimizations complete! Keep monitoring Phase 4 for ongoing improvements.
              </Typography>
            </Box>
          )}

          {/* Phase sections */}
          {playbook.phases.map((phase) => (
            <PlaybookPhase
              key={phase.id}
              phase={phase}
              defaultExpanded={phase.id === firstIncompletePhaseId}
              onStatusChange={onStatusChange}
              onGetFixGuidance={onGetFixGuidance}
              onGetSuggestions={onGetSuggestions}
              onApplyToWordPress={onApplyToWordPress}
              wpConnected={wpConnected}
              rankingPageUrl={rankingPageUrl}
              currentMetrics={currentMetrics}
              onGenerateStepSuggestions={onGenerateStepSuggestions}
              onAddTask={onAddTask}
              existingTasks={existingTasks}
            />
          ))}

          {/* Unmapped findings */}
          {playbook.unmapped.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Other Findings
              </Typography>
              {playbook.unmapped.map((opp) => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  onStatusChange={onStatusChange}
                      onGetFixGuidance={onGetFixGuidance}
                  onGetSuggestions={onGetSuggestions}
                  onApplyToWordPress={onApplyToWordPress}
                  onAddTask={onAddTask}
                  existingTasks={existingTasks}
                  wpConnected={wpConnected}
                  rankingPageUrl={rankingPageUrl}
                  currentMetrics={currentMetrics}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default KeywordOpportunitiesComponent;
